import { useState } from 'react';
import { AlertTriangle, Download } from 'lucide-react';
import { Modal } from '../Modal';
import { api } from '../../lib/api';

interface Props {
  onClose: () => void;
}

interface InutResposta {
  ok: boolean;
  cStat: string | null;
  xMotivo: string;
  nProt: string | null;
  dhRecbto: string | null;
  chave: string;
  xml_proc: string | null;
  xml_retorno: string;
}

export function ModalInutilizarNumeracao({ onClose }: Props) {
  const [modelo, setModelo] = useState<'55' | '65'>('65');
  const [serie, setSerie] = useState('020');
  const [numIni, setNumIni] = useState<number | ''>('');
  const [numFim, setNumFim] = useState<number | ''>('');
  const [ano, setAno] = useState<number>(new Date().getFullYear());
  const [justificativa, setJustificativa] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [resultado, setResultado] = useState<InutResposta | null>(null);

  const valido =
    !!serie.trim() &&
    Number(numIni) > 0 &&
    Number(numFim) >= Number(numIni) &&
    ano > 2000 &&
    justificativa.trim().length >= 15;

  async function confirmar() {
    if (!valido) return;
    setErro('');
    setLoading(true);
    try {
      const data = await api.post<InutResposta>('/api/nfe/inutilizar', {
        modelo,
        serie: serie.trim(),
        numero_inicial: Number(numIni),
        numero_final: Number(numFim),
        ano,
        justificativa: justificativa.trim(),
      });
      setResultado(data);
      if (!data.ok && data.cStat !== '563') {
        setErro(`SEFAZ rejeitou: ${data.xMotivo} (cStat ${data.cStat})`);
      }
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  function baixarXml() {
    if (!resultado?.xml_proc) return;
    const blob = new Blob([resultado.xml_proc], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inut_${modelo}_${serie}_${numIni}-${numFim}.xml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const sucesso = resultado && (resultado.ok || resultado.cStat === '563');

  return (
    <Modal
      aberto
      onFechar={onClose}
      titulo="Inutilizar Numeracao na SEFAZ"
      largura={560}
      rodape={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onClose} disabled={loading} className="btn-secundario">
            {sucesso ? 'Fechar' : 'Cancelar'}
          </button>
          {!sucesso && (
            <button
              type="button"
              onClick={() => void confirmar()}
              disabled={!valido || loading}
              className="btn-primario"
            >
              {loading ? 'Enviando SEFAZ...' : 'Inutilizar'}
            </button>
          )}
        </div>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        {!sucesso && (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 6, padding: 10, fontSize: 12, color: '#854d0e' }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <strong>Acao definitiva.</strong> A SEFAZ marca a numeracao como inutilizada — o numero nunca mais podera ser emitido. Use so pra numeros rejeitados ou abandonados.
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Modelo</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                  <input type="radio" name="modelo" value="65" checked={modelo === '65'} onChange={() => setModelo('65')} disabled={loading} />
                  NFC-e (65)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                  <input type="radio" name="modelo" value="55" checked={modelo === '55'} onChange={() => setModelo('55')} disabled={loading} />
                  NF-e (55)
                </label>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Serie</label>
                <input
                  type="text"
                  value={serie}
                  onChange={(e) => setSerie(e.target.value)}
                  placeholder="020 (atual) ou 010 (legado)"
                  style={{ width: '100%', border: '1px solid var(--input-border)', borderRadius: 4, padding: '6px 8px', fontSize: 13 }}
                  disabled={loading}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Ano</label>
                <input
                  type="number"
                  value={ano}
                  onChange={(e) => setAno(Number(e.target.value))}
                  min={2000}
                  max={2100}
                  style={{ width: '100%', border: '1px solid var(--input-border)', borderRadius: 4, padding: '6px 8px', fontSize: 13 }}
                  disabled={loading}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Numero inicial</label>
                <input
                  type="number"
                  value={numIni}
                  onChange={(e) => setNumIni(e.target.value ? Number(e.target.value) : '')}
                  min={1}
                  style={{ width: '100%', border: '1px solid var(--input-border)', borderRadius: 4, padding: '6px 8px', fontSize: 13 }}
                  disabled={loading}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Numero final</label>
                <input
                  type="number"
                  value={numFim}
                  onChange={(e) => setNumFim(e.target.value ? Number(e.target.value) : '')}
                  min={1}
                  style={{ width: '100%', border: '1px solid var(--input-border)', borderRadius: 4, padding: '6px 8px', fontSize: 13 }}
                  disabled={loading}
                />
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Pra inutilizar 1 numero so, deixe inicial = final. Pra faixa, ex: 19 a 23.
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                Justificativa <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(min. 15 caracteres)</span>
              </label>
              <textarea
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value.slice(0, 240))}
                placeholder="Ex: Notas rejeitadas pela SEFAZ por erro no preenchimento de pagamento"
                rows={3}
                style={{ width: '100%', border: '1px solid var(--input-border)', borderRadius: 4, padding: '6px 8px', fontSize: 13, resize: 'vertical' }}
                disabled={loading}
              />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', marginTop: 2 }}>
                {justificativa.trim().length} caracteres
              </div>
            </div>

            {erro && (
              <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 4, padding: 8, fontSize: 12, color: '#991b1b' }}>
                {erro}
              </div>
            )}
          </>
        )}

        {sucesso && resultado && (
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: 6, padding: 12, fontSize: 13, color: '#14532d' }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                {resultado.cStat === '102' ? 'Inutilizacao homologada!' : 'Ja estava inutilizada antes'}
              </div>
              <div style={{ fontSize: 12 }}>{resultado.xMotivo}</div>
            </div>
            <div style={{ display: 'grid', gap: 4, fontSize: 12, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 4, padding: 10 }}>
              <div><strong>Modelo:</strong> {modelo} ({modelo === '65' ? 'NFC-e' : 'NF-e'})</div>
              <div><strong>Serie:</strong> {serie} &nbsp; <strong>Numeros:</strong> {numIni}{numIni !== numFim ? ` a ${numFim}` : ''}</div>
              <div><strong>Protocolo:</strong> <span style={{ fontFamily: 'monospace' }}>{resultado.nProt || '—'}</span></div>
              <div><strong>Chave:</strong> <span style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>{resultado.chave}</span></div>
            </div>
            {resultado.xml_proc && (
              <button
                type="button"
                onClick={baixarXml}
                className="btn-primario"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}
              >
                <Download size={16} /> Baixar XML procInutNFe
              </button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
